/**
 * Build "Section 1 > Step 2 > …" labels by matching version history rows to
 * `allNodesData` (RenderRevisionNodes tree). Primary match: `row.version_id`
 * equals a node's `version_id_ref`, plus `version_content` tree refs.
 */

function normId(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return null;
  return s;
}

function addToSet(set, v) {
  const n = normId(v);
  if (n) set.add(n);
}

/** Collect every id we might see on a version history row that could tie to a tree node. */
export function collectTargetIdsFromVersionHistoryRow(row) {
  const set = new Set();
  if (!row || typeof row !== "object") return set;
  addToSet(set, row.version_id);
  addToSet(set, row.parent_version);
  const mergeVc = (vc) => {
    if (!vc || typeof vc !== "object") return;
    for (const k of [
      "section_id_ref",
      "step_id_ref",
      "substep_id_ref",
      "sub_step_tree_node_id",
      "section_tree_node_id",
      "step_tree_node_id",
      "canvas_tree_node_id",
      "attachment_tree_node_id",
      "video_tree_node_id",
      "rich_text_node_id",
      "tree_node_id",
      "element_id",
    ]) {
      addToSet(set, vc[k]);
    }
    for (const k of Object.keys(vc)) {
      if (/_id$|_ref$|_node_id$|TreeNode|tree_node/i.test(k)) {
        addToSet(set, vc[k]);
      }
    }
    if (vc.detail && typeof vc.detail === "object") {
      for (const v of Object.values(vc.detail)) {
        if (typeof v === "string" && v.length > 8) addToSet(set, v);
      }
    }
  };
  mergeVc(row.version_content);
  mergeVc(row.parent_version_data?.version_content);
  return set;
}

function sortSectionsLikeRenderer(sections) {
  if (!Array.isArray(sections)) return [];
  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    const safeA = Number.isFinite(intA) ? intA : 0;
    const safeB = Number.isFinite(intB) ? intB : 0;
    return safeA - safeB;
  };
  return [...sections].sort((a, b) => {
    const orderA = a.sibling_order ?? a.detail?.sibling_order ?? a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order ?? b.detail?.sibling_order ?? b.procedure_tree?.sibling_order;
    return compareSiblingOrders(orderA, orderB);
  });
}

function getGlobalStepStart(sortedSections, sectionIndex) {
  let t = 0;
  for (let i = 0; i < sectionIndex; i += 1) {
    t += sortedSections[i]?.steps?.length || 0;
  }
  return t;
}

function refIdsOnNode(node) {
  const out = new Set();
  if (!node) return out;
  addToSet(out, node.version_id_ref);
  addToSet(out, node.tree_node_id);
  addToSet(out, node.section_id_ref);
  addToSet(out, node.step_id_ref);
  addToSet(out, node.substep_id_ref);
  const d = node.detail || {};
  addToSet(out, d.version_id_ref);
  addToSet(out, d.section_tree_node_id);
  addToSet(out, d.step_tree_node_id);
  addToSet(out, d.sub_step_tree_node_id);
  addToSet(out, d.canvas_tree_node_id);
  addToSet(out, d.procedure_tree_ref);
  return out;
}

/** Canonical kind bucket for numbering siblings of the same type (attachment, canvas, …). */
function childKindBucket(ch) {
  if (!ch) return "node";
  const t = String(ch.type || "").toLowerCase();
  if (t.includes("video")) return "video";
  if (t.includes("rich")) return "richtext";
  if (t === "form") return "form";
  if (t.includes("canv")) return "canvas";
  if (t.includes("attach")) return "attachment";
  return `other:${t || "unknown"}`;
}

/** Label when node has its own title (no index needed among generics). */
function childDistinctTitle(ch) {
  if (!ch) return null;
  const page = ch.pageTitle || ch.title || ch.short_description;
  if (page && String(page).trim()) return String(page).trim();
  return null;
}

/** Generic fallback name before sibling index suffix. */
function childGenericName(ch) {
  if (!ch) return "Node";
  const t = String(ch.type || "").toLowerCase();
  if (t.includes("video")) return "Video";
  if (t.includes("rich")) return "Rich Text";
  if (t === "form") return "Form";
  if (t.includes("canv")) return "Canvas";
  if (t.includes("attach")) return "Attachment";
  return "Node";
}

function childLabel(ch) {
  return childDistinctTitle(ch) ?? childGenericName(ch);
}

/**
 * Same parent `children` sorted by sibling order (matches procedure tree UX).
 */
function sortChildrenLikeRenderer(children) {
  if (!Array.isArray(children) || children.length === 0) return [];
  return [...children].sort((a, b) => {
    const oa = parseInt(a?.sibling_order ?? a?.detail?.sibling_order ?? 0, 10);
    const ob = parseInt(b?.sibling_order ?? b?.detail?.sibling_order ?? 0, 10);
    const sa = Number.isFinite(oa) ? oa : 0;
    const sb = Number.isFinite(ob) ? ob : 0;
    return sa - sb;
  });
}

/**
 * Stable display label including per-type sibling index under the step/substep:
 * e.g. "Attachment 2", "Rich Text 1", "Canvas Page …" unchanged when titled.
 *
 * @param {object} ch – child node
 * @param {object[]} siblingsSorted – same parent's children sorted by sibling order
 */
function childLabelIndexed(ch, siblingsSorted) {
  const distinct = childDistinctTitle(ch);
  if (distinct) return distinct;

  const list = siblingsSorted || [];
  const idx = list.indexOf(ch);
  if (idx < 0) return childLabel(ch);

  const bucket = childKindBucket(ch);
  let ordinalForBucket = 0;
  for (let i = 0; i <= idx; i += 1) {
    if (childKindBucket(list[i]) === bucket) ordinalForBucket += 1;
  }
  return `${childGenericName(ch)} ${ordinalForBucket}`;
}

function idsForChild(ch) {
  const out = new Set();
  if (!ch) return out;
  addToSet(out, ch.tree_node_id);
  addToSet(out, ch.version_id_ref);
  const d = ch.detail || {};
  addToSet(out, d.version_id_ref);
  addToSet(out, d.canvas_tree_node_id);
  addToSet(out, d.attachment_tree_node_id);
  addToSet(out, d.video_tree_node_id);
  addToSet(out, d.rich_text_node_id);
  addToSet(out, d.element_id);
  return out;
}

function lookupSectionStepLabelsForGlobalSubstep(
  allNodesData,
  globalStepNum,
  substepOrdinal,
) {
  if (!Number.isFinite(globalStepNum) || globalStepNum < 1) return [];
  const sorted = sortSectionsLikeRenderer(allNodesData);
  let cumulative = 0;
  for (let sIdx = 0; sIdx < sorted.length; sIdx++) {
    const steps = sorted[sIdx]?.steps || [];
    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      cumulative += 1;
      if (cumulative === globalStepNum) {
        const substeps = steps[stIdx].substeps || [];
        if (substepOrdinal < 0 || substepOrdinal >= substeps.length) return [];
        return [`Section ${sIdx + 1}`, `Step ${globalStepNum}`];
      }
    }
  }
  return [];
}

/**
 * Nth child of a bucket in traversal order matching {@link resolveNodePathLabelsFromRevisionTree}
 * (step-level children, then each substep's children).
 */
function lookupFullPathToNthChildOfKind(allNodesData, bucket, ordinal) {
  if (!bucket || !Number.isFinite(ordinal) || ordinal < 1) return null;
  const sorted = sortSectionsLikeRenderer(allNodesData);
  let count = 0;
  for (let sIdx = 0; sIdx < sorted.length; sIdx++) {
    const secPath = [`Section ${sIdx + 1}`];
    const gStart = getGlobalStepStart(sorted, sIdx);
    const steps = sorted[sIdx]?.steps || [];
    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      const gsn = gStart + stIdx + 1;
      const stepPath = [...secPath, `Step ${gsn}`];
      const step = steps[stIdx];

      const stepChildrenSorted = sortChildrenLikeRenderer(step.children);
      for (const ch of stepChildrenSorted) {
        if (childKindBucket(ch) === bucket) {
          count += 1;
          if (count === ordinal) {
            return [...stepPath, childLabelIndexed(ch, stepChildrenSorted)];
          }
        }
      }

      const substeps = step.substeps || [];
      for (let subIdx = 0; subIdx < substeps.length; subIdx++) {
        const letter = String.fromCharCode(65 + subIdx);
        const subPath = [...stepPath, `Substep ${gsn}${letter}`];
        const subChildrenSorted = sortChildrenLikeRenderer(substeps[subIdx].children);
        for (const ch of subChildrenSorted) {
          if (childKindBucket(ch) === bucket) {
            count += 1;
            if (count === ordinal) {
              return [...subPath, childLabelIndexed(ch, subChildrenSorted)];
            }
          }
        }
      }
    }
  }
  return null;
}

/** Section ordinal (1-based) that contains global step number `globalStepNum`, or null. */
function lookupSectionLabelForGlobalStep(allNodesData, globalStepNum) {
  if (!Number.isFinite(globalStepNum) || globalStepNum < 1) return null;
  const sorted = sortSectionsLikeRenderer(allNodesData);
  let cumulative = 0;
  for (let sIdx = 0; sIdx < sorted.length; sIdx++) {
    const steps = sorted[sIdx]?.steps || [];
    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      cumulative += 1;
      if (cumulative === globalStepNum) {
        return `Section ${sIdx + 1}`;
      }
    }
  }
  return null;
}

/**
 * Sparse audit paths often omit Section/Step (e.g. "Rev A- > Substep 1A > Video 1" or "… > Attachment 3").
 * Fill from `revisionTreeNodes` using the same step/substep/leaf ordering as the resolver.
 */
export function augmentHistoryPathSegmentsWithTree(segments, revisionTreeNodes) {
  if (!Array.isArray(segments) || segments.length === 0) return segments;
  if (!Array.isArray(revisionTreeNodes) || revisionTreeNodes.length === 0) {
    return segments;
  }

  let segs = [...segments];

  const stepOnlyRe = /^Step\s+(\d+)\s*$/i;
  const iStepOnly = segs.findIndex((s) => stepOnlyRe.test(String(s).trim()));
  if (iStepOnly >= 0) {
    const beforeStep = segs.slice(0, iStepOnly);
    const hasSecBeforeStep = beforeStep.some((s) =>
      /^Section\s+\d+/i.test(String(s).trim()),
    );
    if (!hasSecBeforeStep) {
      const m = String(segs[iStepOnly]).trim().match(stepOnlyRe);
      if (m) {
        const gsn = parseInt(m[1], 10);
        const secLabel = lookupSectionLabelForGlobalStep(
          revisionTreeNodes,
          gsn,
        );
        if (secLabel) segs.splice(iStepOnly, 0, secLabel);
      }
    }
  }

  const subRe = /^Substep\s+(\d+)([A-Za-z])\s*$/i;
  const iSub = segs.findIndex((s) => subRe.test(String(s).trim()));
  if (iSub >= 0) {
    const before = segs.slice(0, iSub);
    const hasSec = before.some((s) =>
      /^Section\s+\d+/i.test(String(s).trim()),
    );
    const hasSt = before.some((s) => /^Step\s+\d+/i.test(String(s).trim()));
    if (!hasSec || !hasSt) {
      const m = String(segs[iSub]).trim().match(subRe);
      if (m) {
        const gsn = parseInt(m[1], 10);
        const subOrd = String(m[2]).toUpperCase().charCodeAt(0) - 65;
        const prefix = lookupSectionStepLabelsForGlobalSubstep(
          revisionTreeNodes,
          gsn,
          subOrd,
        );
        if (prefix.length >= 2) {
          const ins = [];
          if (!hasSec) ins.push(prefix[0]);
          if (!hasSt) ins.push(prefix[1]);
          if (ins.length) segs.splice(iSub, 0, ...ins);
        }
      }
    }
  }

  const leafRe =
    /^(Attachment|Video|Rich\s+Text|Canvas)\s+(\d+)\s*$/i;
  const iLeaf = segs.findIndex((s) => leafRe.test(String(s).trim()));
  if (iLeaf >= 0) {
    const beforeLeaf = segs.slice(0, iLeaf);
    const hasSec = beforeLeaf.some((s) =>
      /^Section\s+\d+/i.test(String(s).trim()),
    );
    const hasSt = beforeLeaf.some((s) => /^Step\s+\d+/i.test(String(s).trim()));
    if (!hasSec || !hasSt) {
      const m = String(segs[iLeaf]).trim().match(leafRe);
      if (m) {
        const ord = parseInt(m[2], 10);
        const kindStr = String(m[1]).toLowerCase();
        let bucket = null;
        if (kindStr.includes("attach")) bucket = "attachment";
        else if (kindStr.includes("video")) bucket = "video";
        else if (kindStr.includes("rich")) bucket = "richtext";
        else if (kindStr.includes("canvas")) bucket = "canvas";
        if (bucket && ord >= 1) {
          const full = lookupFullPathToNthChildOfKind(
            revisionTreeNodes,
            bucket,
            ord,
          );
          if (full && full.length) {
            segs = [...beforeLeaf, ...full];
          }
        }
      }
    }
  }

  return segs;
}

/**
 * @param {Array} allNodesData - sections with steps (RenderRevisionNodes / getRevAllNodes)
 * @param {object} row - version history row
 * @returns {string[]|null} path segments, or null if no match
 */
export function resolveNodePathLabelsFromRevisionTree(allNodesData, row) {
  if (!Array.isArray(allNodesData) || allNodesData.length === 0) return null;
  const targets = collectTargetIdsFromVersionHistoryRow(row);
  if (targets.size === 0) return null;

  const hasMatch = (idSet) => {
    for (const id of idSet) {
      if (id && targets.has(String(id).trim())) return true;
    }
    return false;
  };

  const sorted = sortSectionsLikeRenderer(allNodesData);
  let best = null;
  const consider = (segments) => {
    if (!segments || segments.length === 0) return;
    if (!best || segments.length > best.length) {
      best = segments;
    }
  };

  sorted.forEach((section, sIdx) => {
    const secPath = [`Section ${sIdx + 1}`];
    const sectionIds = refIdsOnNode(section);
    if (hasMatch(sectionIds)) consider(secPath);

    const gStart = getGlobalStepStart(sorted, sIdx);
    (section.steps || []).forEach((step, stIdx) => {
      const gsn = gStart + stIdx + 1;
      const stepPath = [...secPath, `Step ${gsn}`];
      const stepIds = refIdsOnNode(step);
      if (hasMatch(stepIds)) consider(stepPath);

      const stepChildrenSorted = sortChildrenLikeRenderer(step.children);
      stepChildrenSorted.forEach((ch) => {
        const chPath = [...stepPath, childLabelIndexed(ch, stepChildrenSorted)];
        if (hasMatch(idsForChild(ch))) consider(chPath);
      });

      (step.substeps || []).forEach((sub, subIdx) => {
        const letter = String.fromCharCode(65 + subIdx);
        const subPath = [...stepPath, `Substep ${gsn}${letter}`];
        const subIds = refIdsOnNode(sub);
        if (hasMatch(subIds)) consider(subPath);

        const subChildrenSorted = sortChildrenLikeRenderer(sub.children);
        subChildrenSorted.forEach((ch) => {
          const chPath = [...subPath, childLabelIndexed(ch, subChildrenSorted)];
          if (hasMatch(idsForChild(ch))) consider(chPath);
        });
      });
    });
  });

  return best;
}
